import { notification } from 'antd';
import { IReceitasConfiguracoes } from '../interface';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import CodigoReceitasService from '../../../services/http/services/ConfiguracoesReceitas/CodigoReceitasService';

export type ReceitaType = 'CodReceita';

export const useReceitas = (type: ReceitaType) => {
    const [data, setData] = useState<IReceitasConfiguracoes[]>([]);
    const [qtdReceitas, setQtdReceitas] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [loadingUpdate, setLoadingUpdate] = useState(false);

    const cancelSourceRef = useRef(axios.CancelToken.source());

    const apiMap = useMemo(() => ({
        CodReceita: {
            get: CodigoReceitasService.getReceitasCodigo,
            update: CodigoReceitasService.patchReceitasCodigo,
            getQtd: CodigoReceitasService.getQuantidadeReceitasCodigo,
        },
    }), []);

    const getDataReceita = useCallback(async () => {

        setLoading(true);

        try {
            const response = await apiMap[type].get(cancelSourceRef.current.token);
            setData(response);
        } catch (error) {
            if (!axios.isCancel(error)) {
                notification.error({
                    message: 'Erro',
                    description: `Não foi possível buscar as receitas ${type}.`,
                });
            }
        } finally {
            setLoading(false);
        }
    }, [apiMap, type]);

    const getQtdReceitas = useCallback(async () => {
        try {
            const response = await apiMap[type].getQtd(cancelSourceRef.current.token);
            setQtdReceitas(response.quantidade);
        } catch (error) {
            notification.error({
                message: 'Erro',
                description: `Erro ao buscar quantidade ${type}.`,
            });
        }
    }, [apiMap, type]);

    const getReceitaByCodigo = useCallback(async (codigo: string) => {
        try {
            return await CodigoReceitasService.getReceitaByCodigo(codigo, cancelSourceRef.current.token);
        } catch (error) {
            if (!axios.isCancel(error)) {
                notification.error({
                    message: 'Erro',
                    description: `Não foi possível buscar a receita de código ${codigo}.`,
                });
            }
            return null;
        }
    }, []);

    const updateReceita = useCallback(async (codigo: string, values: any) => {
        setLoadingUpdate(true);
        try {
            const response = await apiMap[type].update(
                codigo,
                values,
                cancelSourceRef.current.token,
            );

            const successMsg = response?.message || 'Atualizado com sucesso';
            const successDesc = response?.atualizados !== undefined
                ? `${response.atualizados} atribuição(ões) atualizada(s).`
                : undefined;

            notification.success({
                message: successMsg,
                description: successDesc,
            });

            window.dispatchEvent(new Event('refreshReceitasTable'));
            window.dispatchEvent(new Event('atualizarContagemBalonIPM'));

            return true;
        } catch (error: any) {
            const detailData = error?.response?.data?.detail;

            if (detailData && typeof detailData === 'object') {
                const title = detailData.message || 'Erro ao atualizar';
                const errosList = detailData.erros;

                if (Array.isArray(errosList) && errosList.length > 0) {
                    const errorMsg = errosList
                        .map((e: any) => `• Código ${e.codigo} (${(e.sistema || '').toUpperCase()}): ${e.motivo || ''}`)
                        .join('\n');

                    notification.error({
                        message: title,
                        description: errorMsg,
                        duration: 8,
                    });
                } else {
                    notification.error({
                        message: 'Erro',
                        description: title,
                        duration: 6,
                    });
                }
            } else {
                const message = detailData || error?.message || 'Não foi possível atualizar a receita.';
                notification.error({
                    message: 'Erro',
                    description: String(message),
                    duration: 6,
                });
            }

            return false;
        } finally {
            setLoadingUpdate(false);
        }
    }, [apiMap, type]);

    useEffect(() => {
        getDataReceita();
        getQtdReceitas();

        const handleRefresh = () => {
            getDataReceita();
            getQtdReceitas();
        };

        window.addEventListener('refreshReceitasTable', handleRefresh);
        return () => {
            window.removeEventListener('refreshReceitasTable', handleRefresh);
        };
    }, [getDataReceita, getQtdReceitas]);

    useEffect(() => {
        return () => {
            if (cancelSourceRef.current) {
                cancelSourceRef.current.cancel();
            }
        };
    }, []);

    return {
        data,
        loading,
        qtdReceitas,
        loadingUpdate,
        updateReceita,
        getDataReceita,
        getQtdReceitas,
        getReceitaByCodigo,
    };
};
